import React, { memo, useEffect, useState } from "react";
import { Breadcrumbs, Header, Workspace, Button } from "components/Shared";
import { CheckBox } from "v2/components/shared";
import { useHistory } from "react-router-dom";
import { useSlaConfiguration } from "./hooks/useSlaConfiguration";
import { SlaConfigurationData } from "./types";

type EditableConfig = Omit<SlaConfigurationData, "id" | "company_id" | "updated_at">;

const CHECKBOX_FIELDS: { key: keyof EditableConfig; label: string }[] = [
  {
    key: "email_assignee_on_upcoming",
    label: "When task enters upcoming window: Email assignee",
  },
  {
    key: "reminder_on_at_risk",
    label: "When task becomes at risk: Send reminder to assignee",
  },
  {
    key: "escalate_manager_on_at_risk",
    label: "At 80% of target time: Escalate to manager",
  },
  {
    key: "escalate_portfolio_on_breached",
    label: "When breached: Immediate escalation to portfolio manager",
  },
  {
    key: "escalate_executive_after_24h",
    label: "24 hours overdue: Escalate to executive level",
  },
  {
    key: "send_daily_digest",
    label: "Send daily digest of SLA status to portfolio manager",
  },
];

const SlaConfiguration = () => {
  const history = useHistory();
  const { config, isLoading, loadError, loadErrorMessage, isSaving, unauthorized, save } = useSlaConfiguration();
  const [formState, setFormState] = useState<EditableConfig | null>(null);

  useEffect(() => {
    if (config) {
      setFormState({
        upcoming_window_days: config.upcoming_window_days,
        email_assignee_on_upcoming: config.email_assignee_on_upcoming,
        reminder_on_at_risk: config.reminder_on_at_risk,
        escalate_manager_on_at_risk: config.escalate_manager_on_at_risk,
        escalate_portfolio_on_breached: config.escalate_portfolio_on_breached,
        escalate_executive_after_24h: config.escalate_executive_after_24h,
        send_daily_digest: config.send_daily_digest,
      });
    }
  }, [config]);

  const handleSave = async () => {
    if (formState) {
      await save(formState);
    }
  };

  // When the backend returns 401 (feature flag disabled), render an empty shell.
  if (unauthorized) return <></>;

  if (isLoading || !formState) {
    return (
      <React.Fragment>
        <Breadcrumbs
          items={[
            { title: "sla-monitor", link: "/sla-monitor" },
            { title: "Configuration" },
          ]}
        />
        <Header title="KPI &amp; SLA Configuration" stamp="sla-monitor" />
        <Workspace id="sla-configuration" className="list">
          <div className="py-10 text-center text-black-40">Loading…</div>
        </Workspace>
      </React.Fragment>
    );
  }

  if (loadError && !config?.id) {
    return (
      <React.Fragment>
        <Breadcrumbs
          items={[
            { title: "sla-monitor", link: "/sla-monitor" },
            { title: "Configuration" },
          ]}
        />
        <Header title="KPI &amp; SLA Configuration" stamp="sla-monitor" />
        <Workspace id="sla-configuration" className="list">
          <div className="py-10 text-center text-error-darker">
            {loadErrorMessage || "Failed to load SLA configuration. Please refresh and try again."}
          </div>
        </Workspace>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Breadcrumbs
        items={[
          { title: "sla-monitor", link: "/sla-monitor" },
          { title: "Configuration" },
        ]}
      />
      <Header title="KPI &amp; SLA Configuration" stamp="sla-monitor" />
      <Workspace id="sla-configuration" className="list">
        {/* Notification & Escalation Section */}
        <div className="bg-white rounded shadow-sm px-5 py-4 mb-5">
          <h2 className="text-18 font-bold mb-3">Notification &amp; Escalation Settings</h2>
          <div className="flex flex-col gap-3">
            {CHECKBOX_FIELDS.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-3 cursor-pointer">
                <CheckBox
                  checked={formState[key] as boolean}
                  onChange={(e: any) =>
                    setFormState((s) =>
                      s ? { ...s, [key]: e.target.checked } : s
                    )
                  }
                />
                <span className="text-13 text-black-80">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3">
          <Button onClick={() => history.push("/sla-monitor")}>Cancel</Button>
          <Button kind="primary" onClick={handleSave} disabled={isSaving || loadError}>
            {isSaving ? "Saving…" : "Save Configuration"}
          </Button>
        </div>
      </Workspace>
    </React.Fragment>
  );
};

export default memo(SlaConfiguration);
